import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import { Alert, Empty, ErrorNote, Loading, PageHeader, Pagination, Toolbar } from '@/components/common/Ui'
import Confirm from '@/components/common/Confirm'
import { PERMS, can, isSuperAdmin, useAuth } from '@/store/auth'
import { dateTime } from '@/lib/format'

const PAGE_SIZE = 25

const BLANK = { email: '', full_name: '', password: '', role: 'operator' }

export default function Users() {
  const { user, refresh } = useAuth()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [showDisabled, setShowDisabled] = useState(false)
  const [form, setForm] = useState(null)
  const [resetting, setResetting] = useState(null) // { id, full_name, password }
  const [confirming, setConfirming] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const manage = can(user, PERMS.usersManage)

  const [page, setPage] = useState(1)

  const params = { page, page_size: PAGE_SIZE }
  if (search) params.search = search
  if (roleFilter) params.role = roleFilter
  if (!showDisabled) params.is_active = true

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', params],
    queryFn: () => usersApi.list(params),
  })

  const { data: roles } = useQuery({ queryKey: ['roles'], queryFn: usersApi.roles })

  // Roles are rows, so their labels come from the API rather than a map here.
  const roleLabelFor = (name) =>
    roles?.find((r) => r.role === name)?.label ?? name.replace(/_/g, ' ')
  // The API refuses to let an admin hand out the super admin role, so don't
  // offer it to them: a picker option that always 403s is just a trap.
  const assignable = (roles ?? []).filter((r) => !r.is_superuser || isSuperAdmin(user))

  const done = async (message) => {
    queryClient.invalidateQueries({ queryKey: ['users'] })
    setError('')
    setNotice(message)
    // A change to your own row (a handover, say) rewrites your own menu.
    await refresh().catch(() => {})
  }

  const create = useMutation({
    mutationFn: usersApi.create,
    onSuccess: async (created) => {
      setForm(null)
      await done(`${created.full_name} can now sign in.`)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const update = useMutation({
    mutationFn: ({ id, ...payload }) => usersApi.update(id, payload),
    onSuccess: (updated) => done(`${updated.full_name} updated.`),
    onError: (e) => setError(errorMessage(e)),
  })

  const deactivate = useMutation({
    mutationFn: usersApi.deactivate,
    onSuccess: () => done('Account disabled.'),
    onError: (e) => setError(errorMessage(e)),
  })

  const setPassword = useMutation({
    mutationFn: ({ id, password }) => usersApi.setPassword(id, password),
    onSuccess: () => {
      setResetting(null)
      done('Password updated successfully.')
    },
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <div className="users-page">
      <PageHeader
        title="Portal users"
        subtitle="Who can sign in, and what each of them is allowed to do"
        actions={
          manage && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setForm(BLANK)
                setNotice('')
              }}
            >
              Add user
            </button>
          )
        }
      />

      {roles && (
        <div className="role-legend">
          {roles.map((r) => (
            <div key={r.role} className="role-card">
              <div className="role-card-head">
                <span className={`badge role-${r.role}`}>{r.label}</span>
                <span className="muted">{r.permissions.length} permissions</span>
              </div>
              <p>{r.description}</p>
              <details>
                <summary>What that covers</summary>
                <ul className="perm-list">
                  {r.permissions.map((p) => (
                    <li key={p} className="mono">
                      {p}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          ))}
        </div>
      )}

      <Toolbar>
        <input
          type="search"
          placeholder="Name or email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
        />
        <select
          value={roleFilter}
          onChange={(e) => {
            setRoleFilter(e.target.value)
            setPage(1)
          }}
        >
          <option value="">All roles</option>
          {roles?.map((r) => (
            <option key={r.role} value={r.role}>
              {r.label}
            </option>
          ))}
        </select>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={showDisabled}
            onChange={(e) => {
              setShowDisabled(e.target.checked)
              setPage(1)
            }}
          />
          Include disabled accounts
        </label>
      </Toolbar>

      {!form && !resetting && error && <ErrorNote message={error} />}
      {!error && <Alert tone="success" message={notice} onClose={() => setNotice('')} />}

      <Drawer
        open={Boolean(form)}
        error={error}
        onErrorDismiss={() => setError('')}
        size="wide"
        title="Add user"
        subtitle="They can change the password themselves once they are in."
        onClose={() => setForm(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setForm(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="user-form"
              className="btn btn-primary"
              disabled={create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create user'}
            </button>
          </>
        }
      >
        {form && (
          <form id="user-form" onSubmit={(e) => {
            e.preventDefault()
            create.mutate(form)
          }}>
          <div className="form-grid">
            <label className="field">
              <span>Full name *</span>
              <input
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
              />
            </label>
            <label className="field">
              <span>Email *</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                autoComplete="off"
                required
              />
            </label>
            <label className="field">
              <span>Temporary password *</span>
              <input
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="At least 8 characters, letters and numbers"
                autoComplete="new-password"
                required
              />
            </label>
            <label className="field">
              <span>Role *</span>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {assignable.map((r) => (
                  <option key={r.role} value={r.role}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="hint">
            They can change this password themselves from the account page once they are in.
          </p>
          </form>
        )}
      </Drawer>


      <Drawer
        open={Boolean(resetting)}
        error={error}
        onErrorDismiss={() => setError('')}
        title="Set password"
        subtitle={resetting?.full_name}
        onClose={() => setResetting(null)}
        footer={
          <>
            <button type="button" className="btn" onClick={() => setResetting(null)}>
              Cancel
            </button>
            <button
              type="submit"
              form="password-form"
              className="btn btn-primary"
              disabled={setPassword.isPending}
            >
              {setPassword.isPending ? 'Saving…' : 'Set password'}
            </button>
          </>
        }
      >
        {resetting && (
          <form id="password-form" onSubmit={(e) => {
            e.preventDefault()
            setPassword.mutate(resetting)
          }}>
          <div className="form-grid">
            <label className="field">
              <span>New password for {resetting.full_name} *</span>
              <input
                type="text"
                value={resetting.password}
                onChange={(e) => setResetting({ ...resetting, password: e.target.value })}
                placeholder="At least 8 characters, letters and numbers"
                autoComplete="new-password"
                required
                autoFocus
              />
            </label>
          </div>
          </form>
        )}
      </Drawer>


      {isLoading && <Loading />}
      {isError && <ErrorNote message="Could not load users" onRetry={refetch} />}
      {data && !data.items.length && <Empty icon="👤" title="No users match this filter" />}

      {data && data.items.length > 0 && (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last sign-in</th>
                {manage && <th />}
              </tr>
            </thead>
            <tbody>
              {data.items.map((u) => {
                const self = u.id === user?.id
                return (
                  <tr key={u.id} className={u.is_active ? '' : 'inactive'}>
                    <td className="strong">
                      {u.full_name}
                      {self && <span className="badge warn-sm">you</span>}
                    </td>
                    <td className="mono">{u.email}</td>
                    <td>
                      {manage ? (
                        <select
                          value={u.role}
                          disabled={update.isPending || (u.is_superuser && !isSuperAdmin(user))}
                          onChange={(e) => update.mutate({ id: u.id, role: e.target.value })}
                        >
                          {[
                            ...assignable,
                            // Keep the current value in the list even when it
                            // is one this admin could not pick themselves.
                            ...(assignable.some((r) => r.role === u.role)
                              ? []
                              : [{ role: u.role, label: roleLabelFor(u.role) }]),
                          ].map((r) => (
                            <option key={r.role} value={r.role}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`badge role-${u.role}`}>
                          {roleLabelFor(u.role)}
                        </span>
                      )}
                    </td>
                    <td>{u.is_active ? 'Active' : 'Disabled'}</td>
                    <td>{u.last_login_at ? dateTime(u.last_login_at) : 'Never'}</td>
                    {manage && (
                      <td className="right nowrap">
                    <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => {
                            setNotice('')
                            setResetting({ id: u.id, full_name: u.full_name, password: '' })
                          }}
                        >
                          Set password
                        </button>
                        {u.is_active ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            disabled={self || deactivate.isPending}
                            title={self ? 'You cannot disable your own account' : undefined}
                            onClick={() => {
                              setNotice('')
                              setConfirming(u)
                            }}
                          >
                            Disable
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={update.isPending}
                            onClick={() => update.mutate({ id: u.id, is_active: true })}
                          >
                            Enable
                          </button>
                        )}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
          />
        </>
      )}
      <Confirm
        open={Boolean(confirming)}
        title={`Disable ${confirming?.full_name ?? ''}?`}
        message="They stop being able to sign in straight away, including any session already open. Their history stays."
        confirmLabel="Disable"
        busy={deactivate.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => {
          deactivate.mutate(confirming.id)
          setConfirming(null)
        }}
      />
    </div>
  )
}
